import AuthImage from "../AuthImage";

// mm → px at the mock-up's scale (A4 width 210 mm ≈ 300 px)
const PX_PER_MM = 1.43;
const LOGO_MM = { small: [28, 10], medium: [40, 14], large: [56, 20], xlarge: [76, 27] };

function Logo({ src, size }) {
  if (size === "none") return null;
  const [w, h] = LOGO_MM[size] || LOGO_MM.medium;
  const style = { width: w * PX_PER_MM, height: h * PX_PER_MM };
  if (!src) return <div className="rounded-sm bg-slate-200 shrink-0" style={style} data-testid="pdf-mock-logo-placeholder" />;
  const cls = "object-contain object-left shrink-0";
  return src.startsWith("/api/files/")
    ? <AuthImage path={src} alt="Logo" className={cls} style={style} data-testid="pdf-mock-logo" />
    : <img src={src.startsWith("http") ? src : `${process.env.REACT_APP_BACKEND_URL}${src}`} alt="Logo" className={cls} style={style} data-testid="pdf-mock-logo" />;
}

/** Instant header + footer mock-up of a PDF page for the settings being edited (the real PDF is one click away via Preview). */
export function PdfBrandMockup({ opts, tenant, docLabel = "Sample document", pairs = [] }) {
  const t = tenant || {};
  const pair = pairs.find((p) => p.key === (opts.font_pair || "classic"));
  const name = opts.show_company_name ? (opts.company_name || t.company_name || "Company name") : "";
  const tagline = opts.show_tagline ? (opts.tagline || t.tagline || "") : "";
  const footer = opts.show_footer ? (opts.footer_text || t.footer_text || "") : "";
  const footerLines = footer.split("\n").map((l) => l.trim()).filter(Boolean);
  const credit = !(opts.hide_platform_branding && t.white_label_allowed);
  const accent = (/^#[0-9a-fA-F]{6}$/.test(opts.accent_color || "") ? opts.accent_color : "") || t.primary_color || "#1D4ED8";
  return (
    <div className="rounded-md border border-border bg-slate-100 dark:bg-slate-800 p-3" data-testid="pdf-brand-mockup">
      <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-2">Live preview · header &amp; footer</p>
      <div className="bg-white text-slate-900 shadow-sm rounded-sm mx-auto flex flex-col" style={{ width: 300, minHeight: 400, padding: 21 }}>
        <div className="flex items-center gap-2 min-h-[20px]">
          <Logo src={opts.logo_url || t.logo_url || ""} size={opts.logo_size} />
          <div className="min-w-0">
            {name && <p className="font-bold leading-tight truncate" style={{ color: accent, fontSize: 12, fontFamily: pair?.css_heading }} data-testid="pdf-mock-company">{name}</p>}
            {tagline && <p className="text-[8px] text-slate-500 leading-tight truncate" data-testid="pdf-mock-tagline">{tagline}</p>}
          </div>
        </div>
        <div className="mt-3 h-px" style={{ backgroundColor: accent }} />
        <p className="mt-3 text-[9px] font-semibold text-slate-700" style={{ fontFamily: pair?.css_heading }} data-testid="pdf-mock-doclabel">{docLabel}</p>
        <div className="mt-2 space-y-1.5 flex-1">
          {[90, 100, 72, 100, 88, 60].map((w, i) => <div key={i} className="h-1.5 rounded bg-slate-200" style={{ width: `${w}%` }} />)}
          <div className="h-10 rounded border border-slate-200 mt-3" />
          {[100, 84, 95].map((w, i) => <div key={`b${i}`} className="h-1.5 rounded bg-slate-200" style={{ width: `${w}%` }} />)}
        </div>
        <div className="mt-4 pt-2 border-t border-slate-200 text-[7px] text-slate-500 leading-snug min-h-[14px]" data-testid="pdf-mock-footer">
          {footerLines.map((l, i) => <p key={i}>{l}</p>)}
          {credit && <p data-testid="pdf-mock-credit">Powered by Fixlr</p>}
          {!footerLines.length && !credit && <p className="italic text-slate-300">No footer</p>}
        </div>
      </div>
    </div>
  );
}
